import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  User, Phone, Mail, MessageSquare, Send, Clock, 
  CheckCircle, Calendar, Building2, X 
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactBanker = ({ isOpen, onClose }) => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [requestType, setRequestType] = useState('general');
  const [preferredContact, setPreferredContact] = useState('email');
  const [preferredTime, setPreferredTime] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const requestTypes = [
    { id: 'general', label: 'General Inquiry', description: 'Questions about our services' },
    { id: 'investment', label: 'Investment Consultation', description: 'Discuss investment strategies' },
    { id: 'portfolio', label: 'Portfolio Review', description: 'Review your current holdings' },
    { id: 'financing', label: 'Financing Options', description: 'TIAO/TIMO financing questions' },
    { id: 'treasury', label: 'Treasury Securities', description: 'TIPO product inquiries' },
    { id: 'compliance', label: 'Compliance & Documentation', description: 'KYC, accreditation, legal docs' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/contact-banker`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          user_email: user?.email,
          user_name: user?.name,
          request_type: requestType,
          preferred_contact: preferredContact,
          preferred_time: preferredTime,
          message: message
        })
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success('Your request has been submitted. A Private Banker will contact you shortly.');
      } else {
        toast.error('Failed to submit request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <Card className="bg-[#0d1d35] border-[#1a2d4a] max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <CardHeader className="border-b border-[#1a2d4a]">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5 text-[#b8860b]" />
                Contact Your Private Banker
              </CardTitle>
              <CardDescription className="text-gray-400 mt-1">
                Schedule a consultation with your dedicated Private Banker
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="pt-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl text-white mb-2">Request Submitted</h3>
              <p className="text-gray-400 mb-6">
                Thank you for reaching out. A Private Banker will contact you within 24 hours via your preferred method.
              </p>
              <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a] mb-6">
                <p className="text-sm text-gray-500">Reference Number</p>
                <p className="text-[#b8860b] font-mono">CB-{Date.now().toString(36).toUpperCase()}</p>
              </div>
              <Button onClick={onClose} className="bg-[#b8860b] hover:bg-[#9a7209]">
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Request Type */}
              <div>
                <label className="block text-sm text-[#b8860b] mb-3">How can we help you?</label>
                <div className="grid grid-cols-2 gap-3">
                  {requestTypes.map((type) => (
                    <div
                      key={type.id}
                      onClick={() => setRequestType(type.id)}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        requestType === type.id
                          ? 'bg-[#b8860b]/20 border-[#b8860b]'
                          : 'bg-[#0a1628] border-[#1a2d4a] hover:border-[#b8860b]/50'
                      }`}
                    >
                      <p className="text-white text-sm font-medium">{type.label}</p>
                      <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preferred Contact Method */}
              <div>
                <label className="block text-sm text-[#b8860b] mb-3">Preferred Contact Method</label>
                <div className="flex gap-4">
                  <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer flex-1 ${
                    preferredContact === 'email'
                      ? 'bg-[#b8860b]/20 border-[#b8860b]'
                      : 'bg-[#0a1628] border-[#1a2d4a]'
                  }`}>
                    <input
                      type="radio"
                      name="contact"
                      value="email"
                      checked={preferredContact === 'email'}
                      onChange={(e) => setPreferredContact(e.target.value)}
                      className="hidden"
                    />
                    <Mail className="w-4 h-4 text-[#b8860b]" />
                    <span className="text-white text-sm">Email</span>
                  </label>
                  <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer flex-1 ${
                    preferredContact === 'phone'
                      ? 'bg-[#b8860b]/20 border-[#b8860b]'
                      : 'bg-[#0a1628] border-[#1a2d4a]'
                  }`}>
                    <input
                      type="radio"
                      name="contact"
                      value="phone"
                      checked={preferredContact === 'phone'}
                      onChange={(e) => setPreferredContact(e.target.value)}
                      className="hidden"
                    />
                    <Phone className="w-4 h-4 text-[#b8860b]" />
                    <span className="text-white text-sm">Phone</span>
                  </label>
                  <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer flex-1 ${
                    preferredContact === 'video'
                      ? 'bg-[#b8860b]/20 border-[#b8860b]'
                      : 'bg-[#0a1628] border-[#1a2d4a]'
                  }`}>
                    <input
                      type="radio"
                      name="contact"
                      value="video"
                      checked={preferredContact === 'video'}
                      onChange={(e) => setPreferredContact(e.target.value)}
                      className="hidden"
                    />
                    <Calendar className="w-4 h-4 text-[#b8860b]" />
                    <span className="text-white text-sm">Video Call</span>
                  </label>
                </div>
              </div>

              {/* Preferred Time */}
              <div>
                <label className="block text-sm text-[#b8860b] mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Preferred Time (Optional)
                </label>
                <Input
                  type="text"
                  value={preferredTime}
                  onChange={(e) => setPreferredTime(e.target.value)}
                  placeholder="e.g., Weekdays 9am-5pm EST, Tuesday afternoon"
                  className="bg-[#0a1628] border-[#1a2d4a] text-white"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm text-[#b8860b] mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-1" />
                  Additional Details
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Please provide any additional information that would help us prepare for our conversation..."
                  className="w-full p-3 bg-[#0a1628] border border-[#1a2d4a] rounded-lg text-white placeholder-gray-500 resize-none"
                  rows={4}
                />
              </div>

              {/* Private Banker Info */}
              <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#b8860b]/20 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-[#b8860b]" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Bethereum Private Banking</p>
                    <p className="text-xs text-gray-500">Dedicated support for accredited investors</p>
                    <p className="text-xs text-[#b8860b] mt-1">Response within 24 business hours</p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="flex-1 border-[#1a2d4a] text-gray-400"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-[#b8860b] hover:bg-[#9a7209]"
                  data-testid="submit-contact-banker"
                >
                  {loading ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Request
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactBanker;
